import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Brief 27 oct B.4 (RCA) — le shortcode [product:id:titre] ne transporte pas le
   vendeur : la card communauté naviguait vers `/app/shop/?product=` (vide) et
   la card du Feed supposait que l'auteur du post était le vendeur (faux pour
   un produit partagé par un tiers). Composant UNIQUE : résout le vendeur via
   GET /api/products/{id} puis ouvre la fiche dans la bonne boutique. */
const ProductLinkCard = ({ productId, title, hintSellerId, testId = 'post-product-link' }) => {
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/products/${productId}`, { withCredentials: true })
      .then(({ data }) => { if (alive) setProduct(data); })
      .catch((e) => { if (alive && e?.response?.status === 404) setMissing(true); });
    return () => { alive = false; };
  }, [productId]);

  const open = async (e) => {
    e.stopPropagation();
    if (missing) { toast.error("Ce produit n'est plus disponible"); return; }
    let sellerId = product?.user_id || hintSellerId;
    if (!sellerId) {
      setOpening(true);
      try {
        const { data } = await axios.get(`${API}/products/${productId}`, { withCredentials: true });
        sellerId = data.user_id;
      } catch {
        toast.error("Ce produit n'est plus disponible");
        setOpening(false);
        return;
      }
      setOpening(false);
    }
    navigate(`/app/shop/${sellerId}?product=${productId}`);
  };

  const priceLabel = product
    ? (product.is_free || !product.price ? 'Gratuit' : `${product.price} ${product.currency || 'CHF'}`)
    : null;

  return (
    <button
      type="button"
      onClick={open}
      disabled={missing}
      className={`w-full text-left flex items-center gap-3 p-3 mb-3 rounded-xl border transition-all group ${
        missing
          ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-70 cursor-not-allowed'
          : 'bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800/40 hover:shadow-md cursor-pointer'
      }`}
      data-testid={testId}
    >
      {product?.thumbnail_url ? (
        <img src={product.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
          <Package className="h-5 w-5 text-purple-600 dark:text-purple-300" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-purple-500 dark:text-purple-300">Produit</p>
        <p className="text-sm font-semibold text-purple-900 dark:text-purple-100 truncate">{product?.title || title}</p>
        <p className="text-xs text-purple-500 dark:text-purple-300/80 truncate">
          {missing ? 'Produit retiré de la boutique' : [product?.seller_name, priceLabel].filter(Boolean).join(' · ') || 'Voir dans la boutique'}
        </p>
      </div>
      {opening ? <Loader2 className="h-5 w-5 text-purple-400 animate-spin" /> : <ChevronRight className="h-5 w-5 text-purple-400 group-hover:translate-x-1 transition-transform" />}
    </button>
  );
};

export default ProductLinkCard;
